import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core'
import { Subscription } from 'rxjs'
import { ActivatedRoute, Router } from '@angular/router'
import { Apollo } from 'apollo-angular'
import {
  IKeyword,
  KeywordQueries,
  IAccount,
  IContentType,
  IPriceList,
} from '../../../graphql/queries/keyword.queries'
import { KeywordMutations } from '../../../graphql/mutations/keyword.mutations'
import { successToast } from '../../../toaster'
import { Location } from '@angular/common'
import { KeywordFormComponent } from '../keyword-form/keyword-form.component'
import get from 'lodash-es/get'
import { CustomerActions } from '../../../actions/customer-actions'
import { allowedContentTypes, updateCustomersNavRequestsCount } from '../utils'
import { stateGroupForKeyword } from 'src/logic/keyword-states'
import { SaveKeywordDialog } from '../save-keyword-dialog.component'
import { DEFAULT_DIALOG_OPTIONS } from 'src/modules/shared/constants/default-dialog-options'
import { MatDialog } from '@angular/material/dialog'
import {
  KeywordFormPaymentOptionType,
  PaymentOption,
} from 'src/interfaces/post-credit-payment.interface'
import { UserMutations } from '../../../graphql/mutations/user.mutations'
import { MultipassKeywordFormComponent } from '../keyword-form/multipass-keyword-form.component'

@Component({
  template: `
    <div class="row">
      <div class="col-md-12">
        <h5 class="ml-3">
          {{ editable ? 'Edit' : 'View' }} {{ stateString(keyword) | titlecase }} Request
        </h5>
      </div>
    </div>
    <multipass-keyword-form
      *ngIf="customer?.multipass"
      class="small-horizontal-buffer bottom-buffer"
      [keyword]="keyword"
      [account]="account"
      [customer]="customer"
      [contentTypeId]="contentTypeId"
      [allowedContentTypes]="allowedContentTypes"
      [allContentTypes]="allContentTypes"
      [priceList]="priceList"
      [submitting]="submitting"
      (update)="onUpdateKeyword($event)"
      (cancel)="onCancel()"
      (delete)="onDelete()"
      (startDuplication)="onStartDuplication()"
      #multipasskeywordForm>
    </multipass-keyword-form>
    <keyword-form
      *ngIf="customer && !customer.multipass"
      class="small-horizontal-buffer bottom-buffer"
      [keyword]="keyword"
      [account]="account"
      [customer]="customer"
      [contentTypeId]="contentTypeId"
      [allowedContentTypes]="allowedContentTypes"
      [allContentTypes]="allContentTypes"
      [submitting]="submitting"
      (update)="onUpdateKeyword($event)"
      (cancel)="onCancel()"
      (delete)="onDelete()"
      (startDuplication)="onStartDuplication()"
      #keywordform>
    </keyword-form>
  `,
  selector: 'edit-keyword-page',
})
export class EditKeywordPage implements OnInit, OnDestroy {
  @ViewChild(KeywordFormComponent) keywordForm: KeywordFormComponent
  @ViewChild(MultipassKeywordFormComponent)
  public multipassKeywordForm: MultipassKeywordFormComponent

  customerRouteObs: Subscription
  keywordRouteObs: Subscription
  queryObs: Subscription
  dialogSub: Subscription

  customerId: number = null
  keywordId: number = null
  contentTypeId: number = null
  priceList: IPriceList

  loading: boolean = true
  submitting: boolean = false
  editable: boolean = false
  keyword: IKeyword = null
  account: IAccount = null
  customer = null
  reminderBetterSubmissions: boolean = true
  plan: { code: string; words: number } = null
  allContentTypes: IContentType[]
  allowedContentTypes: IContentType[]

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private apollo: Apollo,
    private keywordMutations: KeywordMutations,
    private location: Location,
    private customerActions: CustomerActions,
    private dialog: MatDialog,
    private userMutations: UserMutations,
    private keywordQueries: KeywordQueries
  ) {}

  stateString(keyword: IKeyword): string {
    if (keyword) {
      return keyword.state === 'paused' ? 'Inactive' : keyword.state
    }
  }

  ngOnInit(): void {
    this.customerRouteObs = this.route.parent.parent.params.subscribe((params) => {
      this.customerId = +params['customerId']

      this.keywordRouteObs = this.route.params.subscribe((params) => {
        this.keywordId = +params['keywordId']

        if (this.customerId && this.keywordId) {
          this.keywordQueries
            .editKeywordPageQuery(this.customerId, this.keywordId, true)
            .subscribe(({ loading, data, stale }) => {
              this.loading = loading
              if (data) {
                this.keyword = data.customer.keyword
                this.editable = !['draft', 'deleted', 'fulfilled'].includes(this.keyword.state)
                this.account = data.account
                this.customer = data.customer
                this.reminderBetterSubmissions =
                  data.currentUser.uiPreferences.reminderBetterSubmissions
                this.plan = data.customer.plan
                this.contentTypeId = this.keyword.contentType.id
                this.priceList = data.customer.subscriptionPriceList
                this.allContentTypes = data.contentTypes
                this.allowedContentTypes = this.customer.multipass
                  ? this.allContentTypes
                  : allowedContentTypes(this.allContentTypes, get(this.plan, 'words'))
              }
            })
        }
      })
    })
  }

  onUpdateKeyword(keywordUpdateObj: {
    body: object
    selectedPaymentOption: PaymentOption<KeywordFormPaymentOptionType>
  }): void {
    this.submitting = true
    const state = <string>keywordUpdateObj.body['state']
    const wasNotActiveOrAssigned = !['active', 'assigned'].includes(this.keyword.state)
    const isActivatedOrAssigned = ['active', 'assigned'].includes(state)

    if (isActivatedOrAssigned && wasNotActiveOrAssigned && this.reminderBetterSubmissions) {
      this.openDialog(keywordUpdateObj)
    } else {
      this.updateKeyword(keywordUpdateObj.body)
    }
  }

  updateKeyword(formValue: object) {
    const kf = this.keywordForm || this.multipassKeywordForm
    this.keywordMutations
      .updateKeyword(
        {
          customerId: this.customerId,
          id: this.keywordId,
          updates: formValue,
        },
        this.customer.multipass
      )
      .then((data) => {
        let navigateKeywordGroup = 'active'
        if (this.keyword.state === formValue['state']) {
          successToast('Request updated')
          navigateKeywordGroup = stateGroupForKeyword(this.keyword)
        } else if (formValue['state'] === 'paused') {
          successToast('Request saved as a draft')
          navigateKeywordGroup = 'paused'
        } else if (kf.reactivatingKeyword()) {
          successToast('Request reactivated')
        } else {
          successToast('Request updated')
        }
        // remove when customer nav uses graphql
        if (data) updateCustomersNavRequestsCount(data, this.customerActions)
        kf.form.reset()
        this.router.navigate(['../../'], {
          relativeTo: this.route,
          queryParams: { group: navigateKeywordGroup },
        })
      })
      .catch((e) => {
        console.log(e)
        this.submitting = false
      })
  }

  openDialog(createKeywordObj: {
    body: object
    selectedPaymentOption: PaymentOption<KeywordFormPaymentOptionType>
  }): void {
    const dialogRef = this.dialog.open(SaveKeywordDialog, {
      ...DEFAULT_DIALOG_OPTIONS,
      data: {
        newRequest: true,
        dontShowAgain: !this.reminderBetterSubmissions,
        contentTypeId: this.contentTypeId,
        message: createKeywordObj.selectedPaymentOption.confirmMessage,
        buttonText: 'Activate',
      },
    })

    this.dialogSub = dialogRef.afterClosed().subscribe((closedFromButton) => {
      if (closedFromButton && (closedFromButton.submit || closedFromButton.dontShowAgain)) {
        this.reminderBetterSubmissions = !closedFromButton.dontShowAgain
        if (!this.reminderBetterSubmissions) {
          this.userMutations.updateUserPreferences({
            reminderBetterSubmissions: this.reminderBetterSubmissions,
          })
        }
        if (closedFromButton.submit) this.updateKeyword(createKeywordObj.body)
      } else {
        this.submitting = false
      }
    })
  }

  onCancel(): void {
    this.router.navigate(['../../'], {
      relativeTo: this.route,
      queryParams: { group: stateGroupForKeyword(this.keyword) },
    })
  }

  onStartDuplication(): void {
    this.router.navigate([`../../new/${this.contentTypeId}`], {
      relativeTo: this.route,
      queryParams: { dupId: this.keyword.id },
    })
  }

  onDelete(): void {
    const id: number = this.keyword.id
    const customerId: number = this.customerId
    this.keywordMutations.deleteKeyword({ customerId, id }).then((data) => {
      if (data) {
        updateCustomersNavRequestsCount(data, this.customerActions)
      }
      const kf = this.keywordForm || this.multipassKeywordForm
      kf.form.reset()
      this.router.navigate(['../../'], {
        relativeTo: this.route,
        queryParams: { group: stateGroupForKeyword(this.keyword) },
      })
    })
  }

  ngOnDestroy(): void {
    if (this.keywordRouteObs) this.keywordRouteObs.unsubscribe()
    if (this.customerRouteObs) this.customerRouteObs.unsubscribe()
    if (this.queryObs) this.queryObs.unsubscribe()
    if (this.dialogSub) this.dialogSub.unsubscribe()
  }
}
